// Runtime config — see docs/RELEASE_FLOW.md §13.
//
// Each env's GitHub Environment variables populate /config.json at deploy
// time; the SPA fetches it at startup. Local dev / tests fall back to
// `import.meta.env.VITE_*` so `pnpm run dev` and unit tests keep working
// without serving config.json.
//
// Exports are live `let` bindings: when loadRuntimeConfig() reassigns
// them, every `import { apiUrl, ... } from '@/config'` consumer
// transparently sees the new value (per the ES module spec). The internal
// `commit()` helper is the only writer.

import * as Sentry from '@sentry/react';
import { z } from 'zod';

// Shape of /config.json as deployed alongside the SPA bundle.
// `.strict()` rejects unknown fields so a typo in the deploy step's jq
// (e.g. `orchUrI`) surfaces as a validation error instead of silently
// leaving the SPA on the build-time default.
//
// `environment` and `tag` are emitted unconditionally by v1_cd-deploy.yaml's
// `Inject env-scoped config.json` step (its base jq filter is
// `{environment:$environment, tag:$tag}`). Every other field's presence
// depends on whether the matching VITE_* key is set in `.env.v1-<env>` —
// the deploy step skips empty values and `REPLACE_WITH_*` placeholders —
// so they MUST be `.optional()` here. Without that, a /config.json that
// happens to omit one of them (e.g. no VITE_SHOW_LOGS) fails
// validation and the SPA silently falls back to build-time defaults from
// `.env`
const remoteConfigSchema = z.object({
  environment: z.string(),
  tag: z.string().optional(),
  apiUrl: z.string().optional(),
  orchUrl: z.string().optional(),
  showLogs: z.union([ z.boolean(), z.string() ]).optional(),
  apiTimeout: z.union([ z.string(), z.number() ]).optional(),
}).strict();

type RemoteConfig = z.infer<typeof remoteConfigSchema>;

/** Fully-resolved config after build-time + remote merge. */
interface ResolvedConfig {
  env: string;
  isProduction: boolean;
  isLocal: boolean;
  apiUrl: string;
  orchUrl: string;
  showLogs: boolean;
  apiTimeout: number | undefined;
}

const CONFIG_URL = '/config.json';

/** Coerce a possibly-undefined string|number to a number, or undefined. */
function toInt(v: string | number | undefined): number | undefined {
  if (v == null || v === '') return undefined;
  const n = typeof v === 'number' ? v : parseInt(v, 10);
  return Number.isNaN(n) ? undefined : n;
}

/** Coerce an unknown value to a boolean. Treats only literal `true` /
 *  `"true"` as true; everything else (including undefined, empty string,
 *  `"false"`, garbage) is false. Parameter is `unknown` rather than a
 *  narrow union because callers may pass `import.meta.env.VITE_*` whose
 *  declared type varies by repo; the runtime check is the source of truth. */
function toBool(v: unknown): boolean | undefined {
  if (v == null || v === '') return undefined;
  return v === true || v === 'true';
}

/** Choose the build-time API URL based on proxy / local / remote mode. */
function pickBuildtimeApiUrl(env: string): string {
  if (import.meta.env.VITE_USE_PROXY_API === 'true') {
    return import.meta.env.VITE_PROXY_API_URL ?? '';
  }
  if (env === 'local') {
    return import.meta.env.VITE_LOCAL_API_URL ?? '';
  }
  return import.meta.env.VITE_API_URL ?? '';
}

/** Read VITE_* and produce a fully-resolved config (the dev/test path). */
function buildtimeDefaults(): ResolvedConfig {
  const env = import.meta.env.VITE_ENVIRONMENT ?? '';
  return {
    env,
    isProduction: env === 'production',
    isLocal: env === 'local',
    apiUrl: pickBuildtimeApiUrl(env),
    orchUrl: import.meta.env.VITE_ORCH_URL ?? '',
    showLogs: toBool(import.meta.env.VITE_SHOW_LOGS) ?? false,
    apiTimeout: toInt(import.meta.env.VITE_API_TIMEOUT),
  };
}

/**
 * Pick a string field from /config.json on top of build-time default.
 * Treats both undefined and empty string as "not present" so an
 * empty/missing field in /config.json doesn't clobber a valid
 * build-time value.
 */
function pick(remote: string | undefined, base: string): string {
  return remote != null && remote !== '' ? remote : base;
}

/**
 * Merge a /config.json snapshot on top of build-time defaults.
 * See `pick()` for the string-field precedence rule. `showLogs` always
 * takes the remote value when present (since `false` is a meaningful
 * value, "use remote" can't be the same as "remote is truthy").
 */
function applyRemote(base: ResolvedConfig, remote: RemoteConfig): ResolvedConfig {
  const env = pick(remote.environment, base.env);
  return {
    env,
    isProduction: env === 'production',
    isLocal: env === 'local',
    apiUrl: pick(remote.apiUrl, base.apiUrl),
    orchUrl: pick(remote.orchUrl, base.orchUrl),
    showLogs: toBool(remote.showLogs) ?? base.showLogs,
    apiTimeout: toInt(remote.apiTimeout) ?? base.apiTimeout,
  };
}

const initial = buildtimeDefaults();

// Live bindings — see file header. `commit()` is the only writer.
export let env: string = initial.env;
export let isProduction: boolean = initial.isProduction;
export let isLocal: boolean = initial.isLocal;
export let apiUrl: string = initial.apiUrl;
export let orchUrl: string = initial.orchUrl;
export let showLogs: boolean = initial.showLogs;
export let apiTimeout: number | undefined = initial.apiTimeout;

/** Push a resolved config into the live-bound exports. */
function commit(c: ResolvedConfig): void {
  env = c.env;
  isProduction = c.isProduction;
  isLocal = c.isLocal;
  apiUrl = c.apiUrl;
  orchUrl = c.orchUrl;
  showLogs = c.showLogs;
  apiTimeout = c.apiTimeout;
}

let loaded = false;

/**
 * Fetch /config.json once and merge it into the exported bindings.
 * Safe to call multiple times — only the first call performs the fetch.
 * On fetch/parse failure, silently keeps the build-time values.
 * On schema-validation failure, logs to console + Sentry and falls back —
 * the build-time defaults are always valid, so a malformed /config.json
 * never breaks the SPA but does surface to operators.
 */
export async function loadRuntimeConfig(): Promise<void> {
  if (loaded) return;
  loaded = true;

  let remote: RemoteConfig | null = null;
  try {
    const res = await fetch(CONFIG_URL, { cache: 'no-store' });
    if (!res.ok) return;
    const parsed = remoteConfigSchema.safeParse(await res.json());
    if (!parsed.success) {
      const issues = parsed.error.issues
        .map(i => `${i.path.join('.') || '(root)'}: ${i.message}`)
        .join('; ');
      const msg = `Invalid /config.json — falling back to build-time defaults. Issues: ${issues}`;
      Sentry.captureException(new Error(msg));
      return;
    }
    remote = parsed.data;
  }
  catch {
    // Network/JSON-parse failure — fall back to build-time values silently.
  }
  if (!remote) return;

  commit(applyRemote(initial, remote));
}
